import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  MapPin,
  Plus,
  RefreshCcw,
  Trash2,
  Pencil,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAddress from "../../Services/useAddress";
import AddressModal from "../AddressModal";
import MapLocationPicker from "../MapLocationPicker";
import { useGlobalContext } from "../../Context/GlobalContext";

const normalizeString = (value) => String(value ?? "").trim();

const syncSavedAddresses = (addresses) => {
  localStorage.setItem("savedAddresses", JSON.stringify(addresses));

  sessionStorage.setItem("savedAddresses", JSON.stringify(addresses));

  window.dispatchEvent(new Event("savedAddressesUpdated"));
};

const safelyParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};



const getStoredCustomerData = () => {
  if (typeof window === "undefined") {
    return {};
  }

  const storageKeys = [
    "chaafoUser",
    "chaafo_user",
    "user",
    "customer",
    "profile",
  ];

  let storedUser = {};

  for (const key of storageKeys) {
    const sessionValue = safelyParseJson(sessionStorage.getItem(key));
    const localValue = safelyParseJson(localStorage.getItem(key));

    if (sessionValue && typeof sessionValue === "object") {
      storedUser = sessionValue;
      break;
    }

    if (localValue && typeof localValue === "object") {
      storedUser = localValue;
      break;
    }
  }

  return {
    customerId:
      normalizeString(sessionStorage.getItem("user_code")) ||
      normalizeString(localStorage.getItem("user_code")) ||
      normalizeString(sessionStorage.getItem("customerId")) ||
      normalizeString(localStorage.getItem("customerId")) ||
      normalizeString(storedUser?.user_code) ||
      normalizeString(storedUser?.customerId) ||
      normalizeString(storedUser?.customer_id),

    customerMobile:
      normalizeString(sessionStorage.getItem("phone")) ||
      normalizeString(localStorage.getItem("phone")) ||
      normalizeString(storedUser?.number) ||
      normalizeString(storedUser?.mobile) ||
      normalizeString(storedUser?.phone),

    restaurantUserId:
      normalizeString(sessionStorage.getItem("restaurantUserId")) || "A1001"
  };
};

const normalizeAddressObject = (value) => {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    const parsedValue = safelyParseJson(value);

    if (parsedValue && typeof parsedValue === "object") {
      return parsedValue?.values || parsedValue;
    }

    return {};
  }

  if (typeof value === "object") {
    return value?.values || value;
  }

  return {};
};

const buildAddressLine = (item = {}) => {
  if (
    typeof item?.address === "string" &&
    normalizeString(item.address) &&
    !safelyParseJson(item.address)
  ) {
    return normalizeString(item.address);
  }

  const addressObject = normalizeAddressObject(
    item?.address_json ||
      item?.addressJson ||
      item?.address ||
      item?.delivery_address,
  );

  const parts = [
    addressObject?.house_number,
    addressObject?.houseNumber,
    addressObject?.flat_number,
    addressObject?.flatNumber,
    addressObject?.tower_number,
    addressObject?.towerNumber,
    addressObject?.floor_number,
    addressObject?.floorNumber,
    addressObject?.address,
    addressObject?.address_line,
    addressObject?.addressLine,
    addressObject?.building_name,
    addressObject?.buildingName,
    addressObject?.landmark_area,
    addressObject?.landmark,
    addressObject?.area,
    addressObject?.city,
    addressObject?.district,
    addressObject?.state,
    addressObject?.in_pin_code,
    addressObject?.pinCode,
    addressObject?.pincode,
  ]
    .map(normalizeString)
    .filter(Boolean);

  return (
    parts.join(", ") ||
    normalizeString(item?.defaultAddress) ||
    normalizeString(item?.default_address) ||
    normalizeString(item?.address_line) ||
    normalizeString(item?.addressLine) ||
    normalizeString(item?.delivery_address) ||
    normalizeString(item?.full_address) ||
    normalizeString(item?.fullAddress)
  );
};

const createNormalizedAddress = ({
  item,
  customer,
  index,
  fallbackTag = "",
}) => {
  const address = buildAddressLine(item);

  if (!address) {
    return null;
  }

  const addressObject = normalizeAddressObject(
    item?.address_json ||
      item?.addressJson ||
      item?.address ||
      item?.delivery_address,
  );

const locationId = normalizeString(
  item?.pk_location_id ||
    item?.location_id ||
    item?.locationId ||
    addressObject?.pk_location_id ||
    addressObject?.location_id ||
    addressObject?.locationId ||
    customer?.pk_location_id ||
    customer?.location_id ||
    customer?.locationId,
);

  const customerName =
    normalizeString(customer?.custName) ||
    normalizeString(customer?.customer_name) ||
    normalizeString(customer?.name) ||
    [
      normalizeString(customer?.first_name),
      normalizeString(customer?.last_name),
    ]
      .filter(Boolean)
      .join(" ");

  const customerMobile =
    normalizeString(customer?.custMobile) ||
    normalizeString(customer?.customer_mobile) ||
    normalizeString(customer?.mobile) ||
    normalizeString(customer?.phone) ||
    normalizeString(customer?.number);

return {
  id:
    locationId ||
    item?.id ||
    item?.address_id ||
    item?.delivery_address_id ||
    item?.customer_address_id ||
    `${normalizeString(
      customer?.id || customer?.customer_id || "customer",
    )}-${index}`,
  pk_location_id: locationId,
  location_id: locationId,
  locationId,

   tagId: item?.tag_id || item?.tagId || "",

   address,

   tag:
     normalizeString(item?.address_tag) ||
     normalizeString(item?.tag) ||
     normalizeString(item?.favourite) ||
     normalizeString(item?.favorite) ||
     normalizeString(item?.type) ||
     fallbackTag,

   landmark:
     normalizeString(item?.landmark_area) ||
     normalizeString(item?.landmark) ||
     normalizeString(addressObject?.landmark_area) ||
     normalizeString(addressObject?.landmark),

   latitude:
     item?.latitude ??
     item?.lat ??
     addressObject?.latitude ??
     addressObject?.lat ??
     "",

   longitude:
     item?.longitude ??
     item?.lng ??
     item?.long ??
     addressObject?.longitude ??
     addressObject?.lng ??
     "",

   isDefault:
     item?.isDefault === true ||
     item?.is_default === true ||
     item?.default === true ||
     String(item?.isDefault || item?.is_default || item?.default || "")
       .trim()
       .toUpperCase() === "Y" ||
     Number(item?.isDefault || item?.is_default || item?.default || 0) === 1,

   customerName,
   customerMobile,
   raw: item,
 };
};

const normalizeCustomerAddresses = (customer = {}, customerIndex = 0) => {
  const sourceAddresses = [];

  const arrayKeys = [
    "addresses",
    "address",
    "deliveryAddresses",
    "delivery_addresses",
    "customerAddresses",
    "customer_addresses",
  ];

  arrayKeys.forEach((key) => {
    const value = customer?.[key];

    if (Array.isArray(value)) {
      value.forEach((addressItem) => {
        sourceAddresses.push(addressItem);
      });
    }
  });

  const defaultAddress =
    customer?.defaultAddress ||
    customer?.default_address ||
    customer?.defaultDeliveryAddress ||
    customer?.default_delivery_address;

  if (defaultAddress) {
  if (
    typeof defaultAddress === "object" &&
    !Array.isArray(defaultAddress)
  ) {
    sourceAddresses.unshift({
      ...defaultAddress,

      pk_location_id:
        defaultAddress?.pk_location_id ||
        defaultAddress?.location_id ||
        defaultAddress?.locationId ||
        "",

      location_id:
        defaultAddress?.pk_location_id ||
        defaultAddress?.location_id ||
        defaultAddress?.locationId ||
        "",

      locationId:
        defaultAddress?.pk_location_id ||
        defaultAddress?.location_id ||
        defaultAddress?.locationId ||
        "",

      isDefault: true,
    });
  } else {
    sourceAddresses.unshift({
      address: defaultAddress,
      tag:
        normalizeString(customer?.tag) ||
        normalizeString(customer?.favourite) ||
        normalizeString(customer?.favorite) ||
        "Home",
      isDefault: true,
    });
  }
}

  if (sourceAddresses.length === 0 && buildAddressLine(customer)) {
    sourceAddresses.push(customer);
  }

  return sourceAddresses
    .map((item, index) =>
      createNormalizedAddress({
        item:
          typeof item === "string"
            ? {
                address: item,
              }
            : item || {},
        customer,
        index: `${customerIndex}-${index}`,
      }),
    )
    .filter(Boolean);
};

const getAllCustomerAddresses = (customerResult) => {
  const customers = Array.isArray(customerResult?.customers)
    ? customerResult.customers
    : customerResult?.customer
      ? [customerResult.customer]
      : [];

  const allAddresses = customers.flatMap((customer, customerIndex) =>
    normalizeCustomerAddresses(customer, customerIndex),
  );

  const addressMap = new Map();

  allAddresses.forEach((item) => {
    const addressKey = normalizeString(item?.address).toLowerCase();

    if (!addressKey) {
      return;
    }

    const existingAddress = addressMap.get(addressKey);

    if (!existingAddress) {
      addressMap.set(addressKey, item);
      return;
    }

    addressMap.set(addressKey, {
      ...existingAddress,
      ...item,
      isDefault: Boolean(existingAddress?.isDefault || item?.isDefault),
      tag:
        item?.tag && item.tag !== "Delivery Address"
          ? item.tag
          : existingAddress?.tag || "Delivery Address",
    });
  });

  return Array.from(addressMap.values()).sort(
    (firstAddress, secondAddress) =>
      Number(Boolean(secondAddress?.isDefault)) -
      Number(Boolean(firstAddress?.isDefault)),
  );
};

export default function SavedAddress({
  customerId: customerIdProp = "",
  customerMobile: customerMobileProp = "",
  restaurantUserId: restaurantUserIdProp = "A1001",
}) {
  const location = useLocation();
 const {
   getCustomerList,
   setDefaultDeliveryAddress,
   updateUserLocationTag,
   deleteUserLocation,
 } = useAddress();
const {
  user,
  selectedRestaurant,
  setSelectedDeliveryAddress,
  setSelectedDeliveryLocation,
  googleMapsApiKey,
} = useGlobalContext();

  const storedCustomerData = useMemo(() => getStoredCustomerData(), []);
  const navigationState = location?.state || {};
  const isFromCheckout = navigationState?.fromCheckout === true;

  const customerId = normalizeString(
    customerIdProp ||
      navigationState?.customerId ||
      user?.user_code ||
      user?.customerId ||
      user?.customer_id ||
      storedCustomerData?.customerId,
  );

  const customerMobile = normalizeString(
    customerMobileProp ||
      navigationState?.customerMobile ||
      navigationState?.custMobile ||
      user?.number ||
      user?.phone ||
      user?.mobile ||
      sessionStorage.getItem("number") ||
      storedCustomerData?.customerMobile,
  );

  const restaurantUserId = "A1001";
const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const [isAddressDetailsOpen, setIsAddressDetailsOpen] = useState(false);

  const [selectedMapLocation, setSelectedMapLocation] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatingTagId, setUpdatingTagId] = useState("");

  const loadCustomerAddresses = useCallback(async () => {
    if (!customerMobile) {
      setCustomer(null);
      setAddresses([]);
      setIsLoading(false);
      setErrorMessage(
        "Customer mobile number is not available. Please login again.",
      );
      return;
    }

    if (!restaurantUserId) {
      setCustomer(null);
      setAddresses([]);
      setIsLoading(false);
      setErrorMessage(
        "Restaurant userid is not available. Please select restaurant again.",
      );
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const result = await getCustomerList({
        digit: customerMobile,
        userid: "A1001",
      });

      const customerList = Array.isArray(result?.customers)
        ? result.customers
        : result?.customer
          ? [result.customer]
          : [];
      setCustomer(customerList[0] || null);

const allAddresses = getAllCustomerAddresses(result);

setAddresses(allAddresses);

syncSavedAddresses(allAddresses);
    } catch (error) {
      console.error("Saved address customer list error:", error);

      setCustomer(null);
      setAddresses([]);
      setErrorMessage(
        error?.message || "Unable to load customer delivery addresses.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [customerMobile, restaurantUserId, getCustomerList]);

  useEffect(() => {
    loadCustomerAddresses();
  }, [loadCustomerAddresses]);

const openAddAddressModal = () => {
  setErrorMessage("");
  setInfoMessage("");

  // Add Address click -> DIRECT MAP MODAL
  setIsMapPickerOpen(true);
};

const handleMapLocationConfirm = (locationData) => {
  if (!locationData) {
    return;
  }

  const address = normalizeString(locationData?.address);

  const latitude = locationData?.latitude;
  const longitude = locationData?.longitude;

  if (
    !address ||
    latitude === undefined ||
    latitude === null ||
    longitude === undefined ||
    longitude === null
  ) {
    toast.error("Please select a valid location.");
    return;
  }

  console.log("ADD ADDRESS MAP LOCATION:", {
    address,
    latitude,
    longitude,
  });

  // Map se selected location save
  setSelectedMapLocation({
    address,
    latitude,
    longitude,
  });

  // First modal close
  setIsMapPickerOpen(false);

  // Address details modal open
  setIsAddressDetailsOpen(true);
};

const handleAddressAdded = async (selectedAddress = {}) => {
  console.log("NEW ADDRESS ADDED:", selectedAddress);

  // Address details modal close
  setIsAddressDetailsOpen(false);

  // Map selected temp data clear
  setSelectedMapLocation(null);

  // Fresh address list
  await loadCustomerAddresses();

  toast.success("Delivery address added successfully.");
};

const handleDeleteAddress = (item) => {
  if (!item) {
    return;
  }

const locationId = normalizeString(item?.pk_location_id);

  console.log("DELETE ADDRESS OBJECT:", item);
  console.log("LOCATION ID USED FOR DELETE:", locationId);

  if (!locationId) {
    toast.error("Location ID is missing for this address.");
    return;
  }

  setAddressToDelete({
    ...item,
   locationId,
  });

  setDeleteModalOpen(true);
};

const confirmDeleteAddress = async () => {
  if (!addressToDelete || isDeleting) {
    return;
  }

  

  const deletedLocationId = normalizeString(
    addressToDelete?.pk_location_id ||
      addressToDelete?.location_id ||
      addressToDelete?.locationId ||
      addressToDelete?.raw?.pk_location_id ||
      addressToDelete?.raw?.location_id,
  );

  if (!deletedLocationId) {
    toast.error(
      "Location ID is missing for this address.",
    );
    return;
  }

  try {
    setIsDeleting(true);
    setErrorMessage("");

    console.log("DELETE API LOCATION ID:", deletedLocationId);

    /*
     * API payload:
     *
     * {
     *   location_id: "1497"
     * }
     */
    const result = await deleteUserLocation({
      locationId: deletedLocationId,
    });

    if (!result?.success) {
      throw new Error(result?.message || "Unable to delete address.");
    }

    // =====================================================
    // CLEAR DELETED ADDRESS FROM LOCAL / SESSION STORAGE
    // ONLY IF THIS ADDRESS IS CURRENTLY SELECTED
    // =====================================================

    const deletedAddress = normalizeString(addressToDelete?.address);

    const currentSelectedAddress = normalizeString(
      localStorage.getItem("selectedDeliveryAddress") ||
        sessionStorage.getItem("selectedDeliveryAddress"),
    );

const isDeletedAddressCurrentlySelected =
  addressToDelete?.isDefault === true ||
  (deletedAddress &&
    currentSelectedAddress &&
    deletedAddress.toLowerCase() === currentSelectedAddress.toLowerCase());

    if (isDeletedAddressCurrentlySelected) {
      // Selected address text
      localStorage.removeItem("selectedDeliveryAddress");
      sessionStorage.removeItem("selectedDeliveryAddress");

      // Selected location coordinates
sessionStorage.removeItem("selectedDeliveryLatitude");
sessionStorage.removeItem("selectedDeliveryLongitude");

      // Selected address details
      localStorage.removeItem("selectedDeliveryAddressDetails");
      sessionStorage.removeItem("selectedDeliveryAddressDetails");

      sessionStorage.removeItem("selectedDeliveryAddressSource");

      // Checkout address
      localStorage.removeItem("chaafo-checkout-address");
      sessionStorage.removeItem("chaafo-checkout-address");

      // Older fallback key, if present
      localStorage.removeItem("deliveryAddress");
      sessionStorage.removeItem("deliveryAddress");

      // Global context clear
      setSelectedDeliveryAddress?.("");

      setSelectedDeliveryLocation?.({
        latitude: "",
        longitude: "",
      });

      console.log(
        "DELETED ACTIVE ADDRESS CLEARED FROM STORAGE:",
        deletedAddress,
      );
    }

    setDeleteModalOpen(false);
    setAddressToDelete(null);

    /*
     * Immediately remove deleted card.
     */
setAddresses((previousAddresses) => {
  const updatedAddresses = previousAddresses.filter((address) => {
    const currentLocationId = normalizeString(
      address?.pk_location_id ||
        address?.location_id ||
        address?.locationId ||
        address?.raw?.pk_location_id ||
        address?.raw?.location_id,
    );

    return currentLocationId !== deletedLocationId;
  });

  syncSavedAddresses(updatedAddresses);

  return updatedAddresses;
});

    /*
     * Fresh list from server.
     */
    await loadCustomerAddresses();

    toast.success(result?.message || "Address deleted successfully.");
  } catch (error) {
    console.error(
      "Delete address error:",
      error,
    );

    toast.error(
      error?.message ||
        "Unable to delete address.",
    );
  } finally {
    setIsDeleting(false);
  }
};

const handleTagChange = (item, newTag) => {
  const locationId = normalizeString(
    item?.pk_location_id || item?.location_id || item?.locationId,
  );

  if (!locationId) {
    toast.error("Location ID is missing for this address.");
    return;
  }

  setAddresses((previousAddresses) =>
    previousAddresses.map((address) => {
      const currentLocationId = normalizeString(
        address?.pk_location_id || address?.location_id || address?.locationId,
      );

      if (currentLocationId !== locationId) {
        return address;
      }

      return {
        ...address,
        pendingTag: newTag,

        // Clear custom value when switching away from Other
        ...(newTag.toLowerCase() !== "other" &&
        newTag.toLowerCase() !== "others"
          ? { pendingCustomTag: "" }
          : {}),
      };
    }),
  );
};
const handleQuickTagSelect = async (item, newTag) => {
  const locationId = normalizeString(
    item?.pk_location_id || item?.location_id || item?.locationId,
  );

  const cleanTag = normalizeString(newTag);

  if (!locationId) {
    toast.error("Location ID is missing for this address.");
    return;
  }

  if (!cleanTag) {
    return;
  }

  // UI me immediately selected tag dikhao
  handleTagChange(item, cleanTag);

  // Other select kiya hai to custom input open hoga.
  // API custom value Save button se call hogi.
  if (cleanTag.toLowerCase() === "other") {
    return;
  }

  try {
    setUpdatingTagId(locationId);

    await updateUserLocationTag({
      locationId,
      tag: cleanTag,
    });

    setAddresses((previousAddresses) => {
      const updatedAddresses = previousAddresses.map((address) => {
        const currentLocationId = normalizeString(
          address?.pk_location_id ||
            address?.location_id ||
            address?.locationId,
        );

        if (currentLocationId !== locationId) {
          return address;
        }

        return {
          ...address,

          tag: cleanTag,

          pendingTag: "",
          pendingCustomTag: "",
        };
      });

      syncSavedAddresses(updatedAddresses);

      return updatedAddresses;
    });

    toast.success("Address tag updated successfully.");
  } catch (error) {
    console.error("Quick address tag update error:", error);

    toast.error(error?.message || "Unable to update address tag.");
  } finally {
    setUpdatingTagId("");
  }
};
const handleUpdateTag = async (item) => {
  const locationId = normalizeString(
    item?.pk_location_id || item?.location_id || item?.locationId,
  );

  const newTag = normalizeString(item?.pendingTag);
  const customTag = normalizeString(item?.pendingCustomTag);

  const isOtherTag =
    newTag.toLowerCase() === "other" || newTag.toLowerCase() === "others";

  if (!locationId) {
    toast.error("Location ID is missing for this address.");
    return;
  }

  if (!newTag) {
    toast.error("Please select an address tag.");
    return;
  }

  if (isOtherTag && !customTag) {
    toast.error("Please enter a custom address label.");
    return;
  }

  const finalTag = isOtherTag ? customTag : newTag;

  try {
    setUpdatingTagId(locationId);

    console.log("===== UPDATE ADDRESS TAG =====");
    console.log("Location ID:", locationId);
    console.log("Selected Tag:", newTag);
    console.log("Custom Tag:", customTag);
    console.log("Final Tag:", finalTag);

    await updateUserLocationTag({
      locationId,
      tag: finalTag,
    });

    setAddresses((previousAddresses) => {
      const updatedAddresses = previousAddresses.map((address) => {
        const currentLocationId = normalizeString(
          address?.pk_location_id ||
            address?.location_id ||
            address?.locationId,
        );

        if (currentLocationId !== locationId) {
          return address;
        }

        return {
          ...address,
          tag: finalTag,
          pendingTag: "",
          pendingCustomTag: "",
        };
      });

      syncSavedAddresses(updatedAddresses);

      return updatedAddresses;
    });

    toast.success("Address tag updated successfully.");
  } catch (error) {
    console.error("Update address tag error:", error);

    toast.error(error?.message || "Unable to update address tag.");
  } finally {
    setUpdatingTagId("");
  }
};

 const handleSetDefault = async (item) => {
   try {
     // 1. Set Default API
     await setDefaultDeliveryAddress({
       userid: restaurantUserId,
       custMobile: customerMobile,
       address: item.address,
     });

     // 2. Update global selected address
     setSelectedDeliveryAddress?.(item.address);

     // 3. Save selected/default address
     localStorage.setItem("selectedDeliveryAddress", item.address);
     sessionStorage.setItem("selectedDeliveryAddress", item.address);
     localStorage.setItem("chaafo-checkout-address", item.address);

     sessionStorage.setItem("selectedDeliveryAddressSource", "saved-address");

     localStorage.setItem(
       "selectedDeliveryAddressDetails",
       JSON.stringify({
         ...item,
         isDefault: true,
       }),
     );

     sessionStorage.setItem(
       "selectedDeliveryAddressDetails",
       JSON.stringify({
         ...item,
         isDefault: true,
       }),
     );

     // 4. Immediately move selected address to FIRST position
     setAddresses((previousAddresses) => {
       const selectedAddress = previousAddresses.find(
         (address) =>
           normalizeString(address?.address).toLowerCase() ===
           normalizeString(item?.address).toLowerCase(),
       );

       if (!selectedAddress) {
         return previousAddresses;
       }

       return [
         {
           ...selectedAddress,
           isDefault: true,
         },
         ...previousAddresses
           .filter(
             (address) =>
               normalizeString(address?.address).toLowerCase() !==
               normalizeString(item?.address).toLowerCase(),
           )
           .map((address) => ({
             ...address,
             isDefault: false,
           })),
       ];
     });

     setInfoMessage("Default delivery address updated successfully.");

     // 5. Refresh from API
     // 5. Refresh from API
     const result = await getCustomerList({
       digit: customerMobile,
       userid: "A1001",
     });

    const refreshedAddresses = getAllCustomerAddresses(result);

    setAddresses((previousAddresses) => {
      const selectedAddress =
        refreshedAddresses.find(
          (address) =>
            normalizeString(address?.address).toLowerCase() ===
            normalizeString(item?.address).toLowerCase(),
        ) ||
        previousAddresses.find(
          (address) =>
            normalizeString(address?.address).toLowerCase() ===
            normalizeString(item?.address).toLowerCase(),
        );

      if (!selectedAddress) {
        syncSavedAddresses(refreshedAddresses);
        return refreshedAddresses;
      }

      const updatedAddresses = [
        {
          ...selectedAddress,
          isDefault: true,
        },
        ...refreshedAddresses
          .filter(
            (address) =>
              normalizeString(address?.address).toLowerCase() !==
              normalizeString(item?.address).toLowerCase(),
          )
          .map((address) => ({
            ...address,
            isDefault: false,
          })),
      ];

      // IMPORTANT:
      // Keep Dashboard / Checkout / SavedAddress in sync
      syncSavedAddresses(updatedAddresses);

      return updatedAddresses;
    });
   } catch (error) {
     console.error("Set default address error:", error);
     setErrorMessage(error?.message || "Unable to set default address.");
   }
 };

const handleSelectAddress = async (item) => {
  try {
    setInfoMessage("");
    setErrorMessage("");

    const selectedAddressText = normalizeString(item?.address);

    if (!selectedAddressText) {
      setErrorMessage("Address is missing.");
      return;
    }

    console.log("===== SELECTED DELIVERY ADDRESS =====");
    console.log("Selected Address:", item);
    console.log("Restaurant User ID:", restaurantUserId);
    console.log("Customer Mobile:", customerMobile);

    // =====================================================
    // 1. MAKE SELECTED ADDRESS DEFAULT ON SERVER
    // =====================================================

    await setDefaultDeliveryAddress({
      userid: restaurantUserId,
      custMobile: customerMobile,
      address: selectedAddressText,
    });

    // =====================================================
    // 2. UPDATE GLOBAL CONTEXT
    // =====================================================

    setSelectedDeliveryAddress?.(selectedAddressText);

    setSelectedDeliveryLocation?.({
      latitude: item?.latitude ?? "",
      longitude: item?.longitude ?? "",
    });

    // =====================================================
    // 3. SAVE SELECTED ADDRESS TEXT
    // =====================================================

    localStorage.setItem("selectedDeliveryAddress", selectedAddressText);

    sessionStorage.setItem("selectedDeliveryAddress", selectedAddressText);

    // =====================================================
    // IMPORTANT
    // Checkout ko batao ki ye navbar GPS address nahi,
    // SAVED ADDRESS hai.
    // =====================================================

    sessionStorage.setItem("selectedDeliveryAddressSource", "saved-address");

    localStorage.setItem("selectedDeliveryAddressSource", "saved-address");

    // =====================================================
    // 4. SAVE COMPLETE ADDRESS DETAILS
    // =====================================================

    const selectedAddressDetails = {
      ...item,
      address: selectedAddressText,
      source: "saved-address",
    };

    localStorage.setItem(
      "selectedDeliveryAddressDetails",
      JSON.stringify(selectedAddressDetails),
    );

    sessionStorage.setItem(
      "selectedDeliveryAddressDetails",
      JSON.stringify(selectedAddressDetails),
    );

    // Checkout fallback
    localStorage.setItem("chaafo-checkout-address", selectedAddressText);

    sessionStorage.setItem("chaafo-checkout-address", selectedAddressText);

    // =====================================================
    // 5. SAVE LAT / LNG
    // =====================================================

    if (
      item?.latitude !== undefined &&
      item?.latitude !== null &&
      item?.latitude !== ""
    ) {
      sessionStorage.setItem("selectedDeliveryLatitude", String(item.latitude));
    }

    if (
      item?.longitude !== undefined &&
      item?.longitude !== null &&
      item?.longitude !== ""
    ) {
      sessionStorage.setItem(
        "selectedDeliveryLongitude",
        String(item.longitude),
      );
    }

    // =====================================================
    // 6. GO TO CHECKOUT
    // =====================================================

    navigate("/checkout?startCart=1", {
      state: {
        selectedAddress: selectedAddressDetails,
      },
    });
  } catch (error) {
    console.error("Select delivery address error:", error);

    setErrorMessage(error?.message || "Unable to update delivery address.");
  }
};
  return (
    <>
      <div
        className={`flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm ${
          !isLoading && addresses.length <= 2
            ? "h-auto"
            : "h-[calc(100vh-120px)]"
        }`}
      >
        <div className="shrink-0 border-b border-gray-100 bg-white px-4 py-5 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800 sm:text-[28px]">
                Saved Addresses
              </h2>
            </div>

            <div className="flex w-full flex-row gap-2 sm:w-auto">
              <button
                type="button"
                onClick={openAddAddressModal}
                className="inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-blue-600 px-3 text-sm font-semibold text-white transition hover:bg-blue-700 sm:flex-none sm:px-5"
              >
                <Plus size={17} />
                Add Address
              </button>
              <button
                type="button"
                onClick={loadCustomerAddresses}
                disabled={isLoading}
                className="inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:px-5"
              >
                <RefreshCcw
                  size={16}
                  className={isLoading ? "animate-spin" : ""}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div
          className={`hide-scrollbar px-4 py-5 sm:px-6 ${
            !isLoading && addresses.length <= 2
              ? "overflow-visible"
              : "flex-1 overflow-y-auto"
          }`}
        >
          {infoMessage && (
            <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-sm font-medium text-emerald-700">
                {infoMessage}
              </p>
            </div>
          )}

          {errorMessage && (
            <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-red-600">{errorMessage}</p>

              <button
                type="button"
                onClick={loadCustomerAddresses}
                className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full border border-red-200 bg-white px-4 text-sm font-medium text-red-600 transition hover:bg-red-100"
              >
                <RefreshCcw size={15} />
                Retry
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 text-[#8A9BB5]">
              <Loader2 size={32} className="animate-spin" />
              <p className="text-sm">Loading all delivery addresses...</p>
            </div>
          ) : addresses.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {addresses.map((item, index) => (
                  <div
                    key={item?.id || `${item?.address}-${index}`}
                    onClick={() => handleSelectAddress(item)}
                    className={`rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                      isFromCheckout ? "cursor-pointer" : ""
                    }`}
                  >
                    {/* TOP CONTENT */}
                    <div>
                      {/* TAG + CUSTOMER */}
                      <div className="flex items-start gap-4">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                          <MapPin size={14} />
                        </div>

                        <div className="min-w-0 flex-1">
                          {/* CURRENT / SAVED TAG */}
                          <div className="flex items-center">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                                item?.tag?.toLowerCase() === "home"
                                  ? "bg-blue-100 text-blue-700"
                                  : item?.tag?.toLowerCase() === "office"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {item?.tag || "Other"}
                            </span>
                          </div>

                          {/* CUSTOMER */}
                          {item?.customerName && (
                            <p className="mt-3 text-sm font-medium text-gray-700">
                              {item.customerName}
                              {item?.customerMobile
                                ? ` · ${item.customerMobile}`
                                : ""}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* FULL WIDTH ADDRESS */}
                      <p className="mt-2 w-full break-words text-sm leading-5 text-[#8A9BB5] sm:text-[14px]">
                        {item?.address}
                      </p>

                      {/* FULL WIDTH LANDMARK */}
                      {item?.landmark && (
                        <p className="mt-2 w-full break-words text-sm text-[#8A9BB5]">
                          <span className="font-medium text-gray-600">
                            Landmark:
                          </span>{" "}
                          {item.landmark}
                        </p>
                      )}
                    </div>

                    {/* ===================================================== */}
                    {/* BOTTOM ACTIONS - DEFAULT / TAG / DELETE */}
                    {/* ===================================================== */}

                    <div
                      className="
    mt-3
    grid
    grid-cols-3
    gap-2
    border-t
    border-gray-100
    pt-3
  "
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* ===================================================== */}
                      {/* DEFAULT */}
                      {/* ===================================================== */}

                      {!item?.isDefault ? (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(item)}
                          className="
        flex
        h-9
        min-w-0
        w-full
        items-center
        justify-center

        rounded-xl
        border
        border-blue-200

        px-2

        text-[10px]
        font-semibold
        leading-tight
        text-blue-600

        transition

        hover:bg-blue-50

        min-[360px]:text-[11px]
        sm:text-xs
      "
                        >
                          <span className="truncate">Set Default</span>
                        </button>
                      ) : (
                        <div
                          className="
        flex
        h-9
        min-w-0
        w-full
        items-center
        justify-center

        rounded-xl
        bg-green-50

        px-2

        text-[10px]
        font-semibold
        leading-tight
        text-green-600

        min-[360px]:text-[11px]
        sm:text-xs
      "
                        >
                          <span className="truncate">Default</span>
                        </div>
                      )}

                      {/* ===================================================== */}
                      {/* EDIT ADDRESS TAG */}
                      {/* Pencil + Current Tag */}
                      {/* Native select pura button ke upar invisible rahega */}
                      {/* ===================================================== */}

                      <div
                        className={`
      relative

      flex
      h-9
      min-w-0
      w-full
      items-center
      justify-center
      gap-1.5

      overflow-hidden

      rounded-xl
      border
      border-gray-200

      bg-white

      px-2

      text-[10px]
      font-semibold
      text-gray-600

      transition

      hover:border-blue-300
      hover:bg-blue-50
      hover:text-blue-600

      min-[360px]:text-[11px]
      sm:text-xs

      ${
        updatingTagId ===
        normalizeString(
          item?.pk_location_id || item?.location_id || item?.locationId,
        )
          ? "pointer-events-none opacity-60"
          : ""
      }
    `}
                      >
                        {updatingTagId ===
                        normalizeString(
                          item?.pk_location_id ||
                            item?.location_id ||
                            item?.locationId,
                        ) ? (
                          <Loader2
                            size={13}
                            className="shrink-0 animate-spin"
                          />
                        ) : (
                          <Pencil
                            size={13}
                            strokeWidth={2}
                            className="shrink-0"
                          />
                        )}

                        <span className="min-w-0 truncate capitalize">
                          {item?.pendingTag || item?.tag || "Tag"}
                        </span>

                        {/* =============================================== */}
                        {/* INVISIBLE SELECT */}
                        {/* Entire Pencil + Tag button clickable rahega */}
                        {/* =============================================== */}

                        <select
                          aria-label="Change address tag"
                          title="Change address tag"
                          value={
                            item?.pendingTag ||
                            (["Home", "Office", "Other"].includes(item?.tag)
                              ? item.tag
                              : "")
                          }
                          disabled={
                            updatingTagId ===
                            normalizeString(
                              item?.pk_location_id ||
                                item?.location_id ||
                                item?.locationId,
                            )
                          }
                          onChange={(e) => {
                            handleQuickTagSelect(item, e.target.value);
                          }}
                          className="
        absolute
        inset-0

        h-full
        w-full

        cursor-pointer

        opacity-0
      "
                        >
                          {!["Home", "Office", "Other"].includes(item?.tag) && (
                            <option value="">
                              {item?.tag || "Select Tag"}
                            </option>
                          )}

                          <option value="Home">Home</option>

                          <option value="Office">Office</option>

                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* ===================================================== */}
                      {/* DELETE */}
                      {/* ===================================================== */}

                      <button
                        type="button"
                        aria-label="Delete address"
                        title="Delete address"
                        onClick={() => handleDeleteAddress(item)}
                        className="
      flex
      h-9
      min-w-0
      w-full
      items-center
      justify-center
      gap-1.5

      rounded-xl
      border
      border-red-200

      px-2

      text-[10px]
      font-semibold
      leading-tight
      text-red-500

      transition

      hover:bg-red-50
      hover:text-red-600

      min-[360px]:text-[11px]
      sm:text-xs
    "
                      >
                        <Trash2 size={14} className="shrink-0" />
                      </button>
                    </div>

                    {/* ===================================================== */}
                    {/* CUSTOM TAG - ONLY WHEN USER SELECTS "OTHER" */}
                    {/* ===================================================== */}

                    {String(item?.pendingTag || "").toLowerCase() ===
                      "other" && (
                      <div
                        className="mt-2 flex gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          autoFocus
                          value={item?.pendingCustomTag || ""}
                          placeholder="Custom tag e.g. Gym"
                          onChange={(e) => {
                            const value = e.target.value;

                            const locationId = normalizeString(
                              item?.pk_location_id ||
                                item?.location_id ||
                                item?.locationId,
                            );

                            setAddresses((previousAddresses) =>
                              previousAddresses.map((address) => {
                                const currentLocationId = normalizeString(
                                  address?.pk_location_id ||
                                    address?.location_id ||
                                    address?.locationId,
                                );

                                if (currentLocationId !== locationId) {
                                  return address;
                                }

                                return {
                                  ...address,
                                  pendingCustomTag: value,
                                };
                              }),
                            );
                          }}
                          className="
        h-9
        min-w-0
        flex-1
        rounded-xl
        border
        border-gray-200
        bg-white
        px-3
        text-xs
        text-gray-700
        outline-none
        transition
        focus:border-blue-400
      "
                        />

                        <button
                          type="button"
                          disabled={
                            updatingTagId ===
                              normalizeString(
                                item?.pk_location_id ||
                                  item?.location_id ||
                                  item?.locationId,
                              ) || !normalizeString(item?.pendingCustomTag)
                          }
                          onClick={() => handleUpdateTag(item)}
                          className="
        h-9
        shrink-0
        rounded-xl
        bg-blue-600
        px-4
        text-xs
        font-semibold
        text-white
        transition
        hover:bg-blue-700
        disabled:cursor-not-allowed
        disabled:opacity-50
      "
                        >
                          {updatingTagId ===
                          normalizeString(
                            item?.pk_location_id ||
                              item?.location_id ||
                              item?.locationId,
                          )
                            ? "Saving..."
                            : "Save"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : !errorMessage ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-5 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#8A9BB5] shadow-sm">
                <MapPin size={26} />
              </div>

              <h3 className="mt-4 text-lg font-semibold text-gray-800">
                No delivery addresses found
              </h3>

              <p className="mt-2 max-w-[380px] text-sm leading-6 text-[#8A9BB5]">
                Add a new delivery address using the same current-location flow.
              </p>

              <button
                type="button"
                onClick={openAddAddressModal}
                className="mt-5 inline-flex h-11 items-center justify-center gap-2 rounded-full bg-blue-600 px-5 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <Plus size={17} />
                Add Address
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <MapLocationPicker
        isOpen={isMapPickerOpen}
        onClose={() => setIsMapPickerOpen(false)}
        onConfirm={handleMapLocationConfirm}
        apiKey={googleMapsApiKey}
        initialLatitude={
          Number(
            sessionStorage.getItem("customerLatitude") ||
              sessionStorage.getItem("selectedDeliveryLatitude") ||
              0,
          ) || undefined
        }
        initialLongitude={
          Number(
            sessionStorage.getItem("customerLongitude") ||
              sessionStorage.getItem("selectedDeliveryLongitude") ||
              0,
          ) || undefined
        }
      />

      <AddressModal
        isOpen={isAddressDetailsOpen}
        onClose={() => {
          setIsAddressDetailsOpen(false);
          setSelectedMapLocation(null);
        }}
        customerId={customerId}
        customerMobile={customerMobile}
        selectedAddress={selectedMapLocation?.address || ""}
        onAddressSelect={handleAddressAdded}
        openDirectAddForm={true}
        savedAddressPath="/account/address"
        initialLocationData={selectedMapLocation}
      />
      {deleteModalOpen && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div
            className="w-full max-w-[400px] overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="flex justify-center pt-7">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                <Trash2 size={28} strokeWidth={2} className="text-red-500" />
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6 pt-5 text-center">
              <h3 className="text-xl font-bold text-gray-900">
                Delete Address?
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-500">
                Are you sure you want to delete this saved address? This action
                cannot be undone.
              </p>

              {/* Address preview */}
              {addressToDelete?.address && (
                <div className="mt-4 rounded-2xl bg-gray-50 px-4 py-3 text-left">
                  <div className="flex items-start gap-2">
                    <MapPin
                      size={17}
                      className="mt-0.5 shrink-0 text-blue-600"
                    />

                    <div className="min-w-0">
                      {addressToDelete?.tag && (
                        <p className="text-xs font-semibold uppercase text-gray-400">
                          {addressToDelete.tag}
                        </p>
                      )}

                      <p className="mt-1 break-words text-sm font-medium leading-5 text-gray-700">
                        {addressToDelete.address}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setAddressToDelete(null);
                  }}
                  className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={confirmDeleteAddress}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Address
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}